/**
 * Kiosk-side poller for remote Datecs POS End-of-Day (daily settlement).
 *
 * The card terminal is only reachable from this machine's local bridge, so the
 * server can't settle it directly. Instead the admin button / nightly cron
 * raises a per-place "pending" flag; this poller (running only on the kiosk)
 * notices it, runs the settlement on the local bridge, optionally prints a
 * report slip, then reports the result back so the server clears the flag.
 *
 * Self-contained, mirrors datecsKioskPayment.js — no Redux/router wiring.
 */

import { API_URL, DATECS_BRIDGE_URL } from '../../constants';
import Auth from '../../utils/Auth';
import { printEndOfDayReport } from './printReceipt';

const POLL_MS = 60 * 1000; // check once a minute
let running = false; // a settlement is in flight (never overlap)
let timer = null;

function currentPlaceId() {
  try {
    return Auth.getOpenedPlace ? Auth.getOpenedPlace() : null;
  } catch (e) {
    return null;
  }
}

function currentPlaceName() {
  try {
    // Best-effort: the visible header, else empty.
    const el = document.querySelector('header h1, .place-name');
    return (el && el.textContent && el.textContent.trim()) || '';
  } catch (e) {
    return '';
  }
}

async function tick(translations) {
  if (running) return;
  const placeId = currentPlaceId();
  if (!placeId) return;

  let pending;
  try {
    const r = await fetch(API_URL + 'datecs/end-of-day/pending?place_id=' + encodeURIComponent(placeId));
    pending = await r.json();
  } catch (e) {
    return; // server unreachable — try again next tick
  }
  if (!pending || !pending.pending) return;

  running = true;
  const printReport = !!pending.printReport;
  let result = null;
  try {
    const r = await fetch(DATECS_BRIDGE_URL + '/end-of-day', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ printReport }),
    });
    result = await r.json();
  } catch (e) {
    // Bridge unreachable / terminal offline. Leave the flag set so it retries
    // on a later tick (e.g. once the terminal is back). Don't report complete.
    running = false;
    return;
  }

  // Optional printed slip on the kiosk printer.
  if (printReport && result) {
    try {
      await printEndOfDayReport(
        {
          placeName: currentPlaceName(),
          ok: !!result.ok,
          status: result.status,
          terminal: result.terminal,
          dateISO: new Date().toISOString(),
        },
        translations
      );
    } catch (e) {
      /* printing is best-effort */
    }
  }

  // Report back so the server clears the pending flag (and logs the outcome).
  try {
    await fetch(API_URL + 'datecs/end-of-day/complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeId, result }),
    });
  } catch (e) {
    /* if this fails the flag stays; a later successful tick will clear it */
  }

  running = false;
}

/**
 * Start polling for pending End-of-Day requests. No-op unless in kiosk mode.
 * @param {object} [getTranslations] function returning current translations
 * @returns {() => void} stop function
 */
export function startDatecsEndOfDayPoller(getTranslations) {
  if (timer) return () => stopDatecsEndOfDayPoller();
  if (!Auth.isKioskMode || !Auth.isKioskMode()) return () => {};

  const run = () => {
    const translations = typeof getTranslations === 'function' ? getTranslations() : null;
    tick(translations).catch(() => {});
  };
  timer = setInterval(run, POLL_MS);
  run(); // also check right away on start
  return () => stopDatecsEndOfDayPoller();
}

export function stopDatecsEndOfDayPoller() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}
